import * as yup from 'yup';
import { safeDateConvert } from './helpers';
import { EmploymentDetailDto } from '../types/agency';

export const registerInitialValues = {
  firstName: '',
  lastName: '',
  email: '',
  password: '',
  acceptTerms: false,
};

export const loginInitialValues = {
  emailOrUsername: '',
  password: '',
};

export const CompanyInitialValues = {
  companyName: '',
  location: '',
};

export const forgotPasswordInitialValue = {
  email: '',
};

export const changePasswordInitialValue = {
  newPassword: '',
  confirmPassword: '',
};

export const changePasswordSchema = yup.object().shape({
  newPassword: yup
    .string()
    .required('Password is required')
    .min(8, 'Password must be at least 8 characters')
    .max(64, 'Password must not exceed 64 characters')
    .matches(
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?])/,
      'Password must contain at least one uppercase letter, one lowercase letter, one number, and one special character',
    ),
  confirmPassword: yup
    .string()
    .required('Please confirm your password')
    .oneOf([yup.ref('newPassword')], 'Passwords must match'),
});

export const CompanyValidationSchema = yup.object().shape({
  companyName: yup
    .string()
    .trim()
    .required('Company name is required')
    .min(3, 'Company name must be at least 3 characters')
    .max(100, "Company name can't be more than 100 characters"),
  location: yup.string().required('Location is required'),
});

export const companyInitlValues = {
  agencyName: '',
  country: '',
};

export const companyDetailsInitialValues = {
  companyName: '',
  companyType: '',
  corporateId: '',
  taxId: '',
  incDate: '',
  regCountry: '',
  addresses: [
    {
      addressLine1: '',
      addressLine2: '',
      city: '',
      officeCountry: '',
      postalCode: '',
      isPrimary: true,
    },
  ],
  pryContactEmail: '',
  alternateEmail: '',
  phoneNumber: '',
};

export const publicProfileInitialValues = {
  publicName: '',
  website: '',
  employeesNo: '',
  yearFounded: '',
  overView: '',
  tagLine: '',
  logo: '',
  image: '',
  video: '',
  linkedInUrl: '',
  domainExpertise: [],
  // agencyServices: [],
  mtTechSpheresDtos: [],
};

export const basicInfoFormInitialValues = {
  // Personal Information
  professionalPhoto: null,
  firstName: '',
  lastName: '',
  gender: '',
  professionalTitle: '',
  emailAddress: '',
  country: '',
  city: '',
  phoneNumber: '',
  aboutMe: '',
  languageProficiency: [],
  employmentDetails: [],
  professionalCertifications: [],
};

export const forgotPasswordSchema = yup.object().shape({
  email: yup
    .string()
    .email('Invalid email format')
    .required('Email is required'),
});

export const registerSchema = yup.object().shape({
  firstName: yup
    .string()
    .trim()
    .min(2, 'First name must be at least 2 characters')
    .max(50, 'First name must not exceed 50 characters')
    .required('First name is required'),
  lastName: yup
    .string()
    .trim()
    .min(2, 'Last name must be at least 2 characters')
    .max(50, 'Last name must not exceed 50 characters')
    .required('Last name is required'),
  email: yup
    .string()
    .email('Invalid email format')
    .required('Email is required'),
  password: yup
    .string()
    .min(8, 'Password must be at least 8 characters')
    .max(64, 'Password must not exceed 64 characters')
    .matches(
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?])/,
      'Password must contain at least one uppercase letter, one lowercase letter, one number, and one special character',
    )
    .required('Password is required'),
  acceptTerms: yup
    .bool()
    .oneOf([true], 'You must accept the terms and conditions')
    .required('You must accept the terms and conditions'),
});

export const loginSchema = yup.object().shape({
  emailOrUsername: yup
    .string()
    .trim()
    .required('Email is required')
    .test(
      'email-or-username',
      'Must be a valid email or username',
      function (value) {
        if (!value) return false;

        // Check if it's a valid email
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (emailRegex.test(value)) {
          return true;
        }

        // Check if it's a valid username
        const usernameRegex = /^[a-zA-Z0-9_.-]{3,30}$/;
        if (usernameRegex.test(value)) {
          return true;
        }

        return false;
      },
    ),
  password: yup
    .string()
    // Optional: .trim() prevents spaces from bypassing the minimum length requirement
    .min(8, 'Password must be at least 8 characters')
    .max(64, 'Password must not exceed 64 characters') // Added maximum length validation
    .required('Password is required'),
});

export const companySchema = yup.object().shape({
  agencyName: yup
    .string()
    .trim()
    .min(2, 'Agency Name must be at least 2 characters')
    .max(50, 'Agency Name must not exceed 50 characters')
    .required('Agency Name is required'),
  country: yup
    .string()
    .trim()
    .min(2, 'Country must be at least 2 characters')
    .max(50, 'Country must not exceed 50 characters')
    .required('Country is required'),
});

const addressSchema = yup.object({
  addressLine1: yup
    .string()
    .trim()
    .min(5, 'Street address must be at least 5 characters')
    .max(100, 'Street address must not exceed 100 characters')
    .required('Street address is required'),
  addressLine2: yup
    .string()
    .trim()
    .max(100, 'Address line 2 must not exceed 100 characters'),
  city: yup
    .string()
    .trim()
    .min(2, 'City must be at least 2 characters')
    .max(50, 'City must not exceed 50 characters')
    .required('City is required'),
  officeCountry: yup.string().required('Country is required'),
  postalCode: yup
    .string()
    .trim()
    .max(10, 'Postal Code must not exceed 10 characters')
    .matches(
      /^[a-zA-Z0-9\s-]+$/,
      'Postal Code must contain only letters, numbers, spaces, or hyphens'
    )
    .required('Postal Code is required'),
  isPrimary: yup.boolean(),
});

export const companyDetailsSchema = yup.object().shape({
  companyName: yup
    .string()
    .trim()
    .min(2, 'Company legal name must be at least 2 characters')
    .max(100, 'Company legal name must not exceed 100 characters')
    .required('Company legal name is required'),

  companyType: yup.string().required('Company type is required'),

  corporateId: yup
    .string()
    .trim()
    .transform((value) => (value ? value.toUpperCase() : value))
    .min(5, 'Business registration number must be at least 5 characters')
    .max(25, 'Business registration number must not exceed 25 characters')
    .matches(
      /^[A-Z0-9\-\/]+$/,
      'Only alphanumeric characters, hyphens (-), and slashes (/) are allowed',
    )
    .required('Business registration number is required'),

  // ✅ OPTIONAL: Tax ID (Validation runs only if value is typed)
  taxId: yup
    .string()
    .trim()
    .transform((value) => (!value ? null : value.toUpperCase())) // Convert empty to null
    .nullable() // Allow null
    .notRequired() // Allow undefined
    .min(5, 'Tax identification number must be at least 5 characters')
    .max(20, 'Tax identification number must not exceed 20 characters')
    .matches(
      /^[A-Z0-9-]+$/,
      'Only alphanumeric characters and hyphens (-) are allowed',
    ),

  incDate: yup.string().required('Date of incorporation is required'),

  regCountry: yup.string().required('Country of registration is required'),

  addresses: yup
    .array()
    .of(addressSchema)
    .min(1, 'At least one address is required'),

  pryContactEmail: yup
    .string()
    .trim()
    .email('Please enter a valid email address')
    .max(100, 'Email Address must not exceed 100 characters')
    .required('Primary contact email is required'),

  alternateEmail: yup
    .string()
    .trim()
    .email('Please enter a valid email address')
    .max(100, 'Email Address must not exceed 100 characters'),

  phoneNumber: yup
    .string()
    .trim()
    .nullable() // Allows null values
    .notRequired() // Marks the field as optional
    .transform((value) => (value === '' ? null : value)) // Converts empty strings to null so validation is skipped
    .matches(/^\+?[\d\s\-\(\)]+$/, {
      message: 'Please enter a valid phone number',
      excludeEmptyString: true, // Skips regex if string is empty
    })
    .test(
      'len',
      'Phone number must be at least 10 digits',
      (val) => !val || val.length >= 10,
    )
    .test(
      'max',
      'Phone number must not exceed 20 characters',
      (val) => !val || val.length <= 20,
    ),
});

export const publicProfileSchema = yup.object().shape({
  publicName: yup
    .string()
    .required('Public agency name is required')
    .min(2, 'Agency name must be at least 2 characters')
    .max(100, 'Agency name must not exceed 100 characters'),

  website: yup
    .string()
    .trim()
    .required('Website url is required')
    .min(2, 'Website Url must be at least 2 characters')
    .matches(
      /^(https?:\/\/)?([\w-]+\.)+[\w-]{2,}(\/[\w\-._~:/?#[\]@!$&'()*+,;=]*)?$/,
      'Please enter a valid website URL',
    ),

  employeesNo: yup.string().required('Number of employees is required'),

  yearFounded: yup.string().required('Year founded is required'),

  overView: yup
    .string()
    .required('Please add a company overview')
    .min(50, 'Overview must be at least 50 characters')
    .max(500, 'Overview must not exceed 500 characters'),

  domainExpertise: yup
    .array()
    .of(yup.string().required())
    .min(1, 'At least one industry is required')
    .required('Industries served is required'),

  mtTechSpheresDtos: yup
    .array()
    .of(yup.string().required())
    .min(1, 'At least one service is required')
    .required('Primary services is required'),

  // ✅ OPTIONAL: Tagline
  tagLine: yup
    .string()
    .trim()
    .min(5, 'Tagline must be at least 5 characters')
    .max(100, 'Tagline must not exceed 100 characters')
    .nullable()
    .optional(),

  logo: yup.mixed().nullable().required('Please upload an image to continue'),

  // ✅ OPTIONAL: Cover Image
  image: yup.mixed().nullable().optional(),

  // ✅ OPTIONAL: Video
  video: yup.mixed().nullable().optional(),

  linkedInUrl: yup
    .string()
    .matches(
      /^(https?:\/\/)?(www\.)?linkedin\.com\/(mwlite\/|m\/)?(in|company)\/[a-zA-Z0-9_.-]+\/?(\?.*)?$/,
      'Please enter a valid LinkedIn URL (e.g. linkedin.com/company/your-agency)',
    )
    .required('LinkedIn profile URL is required'),
});

const languageDtoSchema = yup.object().shape({
  id: yup.number().required('Language ID is required'),
  name: yup.string().optional(),
});

export const basicInfoFormSchema = yup.object().shape({
  // Personal Information Validation
  firstName: yup
    .string()
    .trim()
    .min(2, 'First Name must be at least 2 characters')
    .max(50, 'First Name must not exceed 50 characters')
    .matches(/^[a-zA-Z\s]+$/, 'First Name can only contain letters and spaces')
    .required('First Name is required'),

  lastName: yup
    .string()
    .trim()
    .min(2, 'Last Name must be at least 2 characters')
    .max(50, 'Last Name must not exceed 50 characters')
    .matches(/^[a-zA-Z\s]+$/, 'Last Name can only contain letters and spaces')
    .required('Last Name is required'),

  gender: yup.string().required('Gender is required'),

  professionalTitle: yup
    .string()
    .trim()
    .min(5, 'Professional Title must be at least 5 characters')
    .max(100, 'Professional Title must not exceed 100 characters')
    .required('Professional Title is required'),

  emailAddress: yup
    .string()
    .trim()
    .email('Please enter a valid email address')
    .matches(/@[^.]*\./, 'Please enter a valid email address')
    .max(100, 'Email Address must not exceed 100 characters')
    .required('Email Address is required'),

  country: yup.string().required('Country is required'),

  city: yup
    .string()
    .trim()
    .min(2, 'City must be at least 2 characters')
    .max(50, 'City must not exceed 50 characters')
    .required('City is required'),

  phoneNumber: yup
    .string()
    .trim()
    .matches(/^\+?[\d\s\-\(\)]+$/, 'Please enter a valid phone number')
    .min(10, 'Phone number must be at least 10 digits')
    .max(20, 'Phone number must not exceed 20 characters')
    .required('Phone number is required'),

  aboutMe: yup
    .string()
    .trim()
    .min(50, 'About Me must be at least 50 characters')
    .max(300, 'About Me must not exceed 300 characters')
    .required('About Me is required'),

  languageProficiency: yup
    .array()
    .of(
      yup.object().shape({
        // 1. Check for the language ID inside the nested DTO

        id: yup.string().required('Language is required'),
        // 2. Check for the proficiency level
        langLevel: yup.string().required('Proficiency level is required'),
      }),
    )
    .min(1, 'Please select at least one language')
    .required('Language Proficiency is required'),

  // Employment Details Validation - MADE REQUIRED
  employmentDetails: yup
    .array()
    .of(
      yup.object().shape({
        title: yup
          .string()
          .trim()
          .min(5, 'Job Title must be at least 5 characters')
          .max(100, 'Job Title must not exceed 100 characters')
          .required('Job Title is required'),

        company: yup
          .string()
          .trim()
          .min(2, 'Company name must be at least 2 characters')
          .max(100, 'Company name must not exceed 100 characters')
          .required('Company is required'),

        domains: yup
          .array()
          .of(yup.string())
          .min(1, 'Please select at least one domain')
          .required('Domain is required'),

        // stackList: yup
        //   .array()
        //   .of(yup.string())
        //   .min(1, 'Please select at least one technology')
        //   .required('Tech Stack is required'),
        stackList: yup.array().of(yup.string()).nullable().notRequired(),

        fromYear: yup
          .string()
          .trim()
          .required('Start Date is required')
          .test(
            'start-date-valid',
            'Start Date cannot be in the future',
            function (value) {
              if (!value) return false;

              // Convert the stored DD/MM/YYYY string into a reliable ISO format
              const isoValue = safeDateConvert(value);
              if (!isoValue) return false;

              const parts = isoValue.split('-');
              if (parts.length === 3) {
                const [y, m, d] = parts.map(Number);
                const date = new Date(y, m - 1, d);
                const today = new Date();
                today.setHours(0, 0, 0, 0);
                return !isNaN(date.getTime()) && date <= today;
              }
              const date = new Date(isoValue);
              return !isNaN(date.getTime()) && date <= new Date();
            },
          ),

        // 🚀 CORRECTED toYear VALIDATION
        toYear: yup
          .string()
          .nullable()
          .when('isCurrentlyWorking', {
            is: false,
            then: (schema) =>
              schema
                .required('End Date is required when not currently working')
                .test(
                  'end-after-start',
                  'End Date must be after Start Date',
                  function (value) {
                    const { fromYear } = this.parent;
                    if (!fromYear || !value) return false;

                    const startDateISO = safeDateConvert(fromYear);
                    const endDateISO = safeDateConvert(value);

                    const startDate = new Date(startDateISO);
                    const endDate = new Date(endDateISO);

                    // Check if both dates are valid and the end date is after start date
                    return (
                      !isNaN(startDate.getTime()) &&
                      !isNaN(endDate.getTime()) &&
                      endDate > startDate
                    );
                  },
                ),
            otherwise: (schema) => schema.nullable(),
          }),

        isCurrentlyWorking: yup.boolean(),

        description: yup
          .string()
          .trim()
          .min(50, 'Description must be at least 50 characters')
          .max(1000, 'Description must not exceed 1000 characters')
          .required('Description is required'),
      }),
    )
    .min(1, 'Please add at least one employment detail')
    .required('Employment details are required'),

  // Professional Certifications Validation - KEPT OPTIONAL
  professionalCertifications: yup.array().of(
    yup.object().shape({
      certificateName: yup
        .string()
        .trim()
        .min(1, 'Certificate Name must be at least 5 characters')
        .max(100, 'Certificate Name must not exceed 100 characters')
        .required('Certificate Name is required'),

      certLink: yup
        .string()
        .trim()
        .url('Please enter a valid URL')
        .required('Validation URL is required'),

      certificateFile: yup
        .mixed<File>()
        .nullable()
        // .test(
        //   'file-required',
        //   'Certificate file is required',
        //   function (value) {
        //     const { id, certDto } = this.parent;
        //     // File not required if certification already exists with a document
        //     if ((id && certDto) || certDto) {
        //       return true;
        //     }
        //     // For new certifications, file is required
        //     return value !== null && value !== undefined;
        //   }
        // )
        .test('file-size', 'File size must not exceed 5MB', function (value) {
          if (!value) return true; // Skip if no file
          if (!(value instanceof File)) {
            return true;
          }
          const file = value as File;
          return file.size <= 5 * 1024 * 1024; // 5MB
        })
        .test(
          'file-type',
          'Only PDF, JPG, PNG files are allowed',
          function (value) {
            if (!value) return true; // Skip if no file
            if (!(value instanceof File)) {
              return true;
            }
            const file = value;
            return [
              'application/pdf',
              'image/jpeg',
              'image/jpg',
              'image/png',
            ].includes(file.type);
          },
        ),

      issueDate: yup
        .date()
        .nullable()
        .required('Issue Date is required')
        .test(
          'issue-date-valid',
          'Issue Date cannot be in the future',
          function (value) {
            return value ? value <= new Date() : false;
          },
        ),

      expirationDate: yup
        .date()
        .nullable()
        .test(
          'expiry-after-issue',
          'Expiration Date must be after Issue Date',
          function (value) {
            const { issueDate } = this.parent;
            if (!value) return true; // Expiration date is optional
            return !issueDate || value > issueDate;
          },
        ),
    }),
  ),
  // Professional Photo Validation
  professionalPhoto: yup
    .mixed()
    .required('Professional Photo is required')
    .test('is-file-or-dto', 'Professional Photo is required', (value) => {
      // Must pass if value is a File object OR if it's a previously loaded DTO with content
      if (value instanceof File) return true;

      // Reject if value is an empty object {}
      if (
        typeof value === 'object' &&
        value !== null &&
        Object.keys(value).length === 0
      ) {
        return false;
      }

      // Accept if it's null (if optional) or a non-empty DTO object (for re-edit)
      return !!value;
    }),
});

export const payValidationSchema = yup.object({
  contractRates: yup
    .array()
    .of(
      yup.object({
        available: yup.boolean(),
        hours: yup.number(),

        threeMonth: yup
          .number()
          .required('Rate is required')
          .min(100, 'Minimum rate is USD 100')
          .test(
            'multiple-of-100',
            'Must be multiple of USD 100',
            function (value) {
              const { hours } = this.parent;
              if (value == null) return true;
              if (hours === 8) {
                return value % 100 === 0;
              }
              return true;
            },
          ),

        sixMonth: yup
          .number()
          .required('Rate is required')
          .min(100, 'Minimum rate is USD 100')
          .test(
            'less-than-three',
            'Value must be USD 100 less than 3 Month',
            function (value) {
              const { threeMonth, hours } = this.parent;

              // Only apply this validation for 8-hour contracts
              if (hours !== 8) return true;

              if (value == null || threeMonth == null) return true;
              return value <= threeMonth - 100;
            },
          )
          .test(
            'multiple-of-100',
            'Must be multiple of USD 100',
            function (value) {
              const { hours } = this.parent;
              if (value == null) return true;
              if (hours === 8) {
                return value % 100 === 0;
              }
              return true;
            },
          ),

        twelveMonth: yup
          .number()
          .required('Rate is required')
          .min(100, 'Minimum rate is USD 100')
          .test(
            'less-than-six',
            'Value must be USD 100 less than 6 Month',
            function (value) {
              const { sixMonth, hours } = this.parent;

              // Only apply this validation for 8-hour contracts
              if (hours !== 8) return true;

              if (value == null || sixMonth == null) return true;
              return value <= sixMonth - 100;
            },
          )
          .test(
            'multiple-of-100',
            'Must be multiple of USD 100',
            function (value) {
              const { hours } = this.parent;
              if (value == null) return true;
              if (hours === 8) {
                return value % 100 === 0;
              }
              return true;
            },
          ),
      }),
    )
    // --- THIS IS THE NEW VALIDATION ---
    // Triggers an error on the 'contractRates' array itself if no row is selected
    .test(
      'at-least-one-available',
      'Please select at least one contract duration option.',
      (values) => {
        // Returns true if values is undefined OR at least one item has available=true
        return !values || values.some((item) => item.available === true);
      },
    ),

  openEnded: yup.object({
    hoursPerDay: yup
      .number()
      .transform((value, originalValue) =>
        originalValue === '' ? null : value,
      )
      .test('valid-hours', 'Hours must be between 1 and 24', (value) => {
        return value == null || (value >= 1 && value <= 24);
      }),
    // .test(
    //   'required-if-other-filled',
    //   'This field is required when monthly rate is provided',
    //   function (value) {
    //     const { monthlyRate } = this.parent;
    //     if (monthlyRate != null && monthlyRate !== '' && value == null) {
    //       return false;
    //     }
    //     return true;
    //   }
    // )
    monthlyRate: yup
      .number()
      .transform((value, originalValue) =>
        originalValue === '' ? null : value,
      )
      .test('valid-rate', 'Minimum rate is $100', (value) => {
        return value == null || value >= 100;
      })
      .test('multiple-of-100', 'Must be multiple of $100', (value) => {
        return value == null || value % 100 === 0;
      }),
    // .test(
    //   'required-if-other-filled',
    //   'This field is required when hours per day is provided',
    //   function (value) {
    //     const { hoursPerDay } = this.parent;
    //     if (hoursPerDay != null && hoursPerDay !== '' && value == null) {
    //       return false;
    //     }
    //     return true;
    //   }
    // ),
  }),
});

export const validateEmploymentForm = (
  formData: EmploymentDetailDto,
): Partial<EmploymentDetailDto> => {
  const newErrors: Partial<EmploymentDetailDto> = {};

  // 1. Title Validation
  if (!formData.title.trim()) {
    newErrors.title = 'Job title is required';
  } else if (formData.title.trim().length < 5) {
    newErrors.title = 'Job Title must be at least 5 characters';
  } else if (formData.title.trim().length > 100) {
    newErrors.title = 'Job Title must not exceed 100 characters';
  }

  // 2. Company Validation
  if (!formData.company.trim()) {
    newErrors.company = 'Company is required';
  } else if (formData.company.trim().length < 2) {
    newErrors.company = 'Company name must be at least 2 characters';
  } else if (formData.company.trim().length > 100) {
    newErrors.company = 'Company name must not exceed 100 characters';
  }

  // 3. Domain Validation (Kept Required)
  if (!formData.domains || formData.domains.length === 0) {
    newErrors.domains = ['Domain selection is required'] as any;
  }

  // ❌ REMOVED: Stack List Validation (Now Optional)
  // if (!formData.stackList || formData.stackList.length === 0) {
  //   newErrors.stackList = ['Tech stack is required'] as any;
  // }

  // 4. Date Fields Validation
  const now = new Date();
  now.setHours(0, 0, 0, 0);

  const parseLocal = (dateStr: string) => {
    if (!dateStr) return null;
    const parts = dateStr.includes('-') ? dateStr.split('-') : dateStr.split('/');
    if (parts.length === 3) {
      if (parts[0].length === 4) {
        const [y, m, d] = parts.map(Number);
        return new Date(y, m - 1, d);
      }
      const [d, m, y] = parts.map(Number);
      return new Date(y, m - 1, d);
    }
    return new Date(dateStr);
  };

  if (!formData.fromYear) {
    newErrors.fromYear = 'Start date is required';
  } else {
    const startDate = parseLocal(formData.fromYear);
    if (startDate && startDate > now) {
      newErrors.fromYear = 'Start Date cannot be in the future';
    }
  }

  if (!formData.isCurrentlyWorking) {
    if (!formData.toYear) {
      newErrors.toYear = 'End date is required when not currently working';
    } else {
      const startDate = formData.fromYear ? parseLocal(formData.fromYear) : null;
      const endDate = parseLocal(formData.toYear);

      if (endDate && endDate > now) {
        newErrors.toYear = 'End Date cannot be in the future';
      } else if (startDate && endDate && endDate <= startDate) {
        newErrors.toYear = 'End Date must be after Start Date';
      }
    }
  }

  // 5. Description Validation
  if (!formData.description.trim()) {
    newErrors.description = 'Description is required';
  } else if (formData.description.trim().length < 50) {
    newErrors.description = 'Description must be at least 50 characters';
  } else if (formData.description.trim().length > 1000) {
    newErrors.description = 'Description must not exceed 1000 characters';
  }

  return newErrors;
};

export const billingInitialValues = {
  companyName: 'Workfall Technologies Inc.',
  address1: '1234 Business Avenue',
  address2: 'Suite 100',
  city: 'San Francisco',
  state: 'California',
  zip: '94105',
  country: 'United States',
  taxId: 'US-123456789',
  billingEmail: 'billing@workfall.com',
};

export const billingValidationSchema = yup.object().shape({
  companyName: yup.string().required('Company name is required'),
  address1: yup.string().required('Address 1 is required'),
  address2: yup.string(),
  city: yup.string().required('City is required'),
  state: yup.string().required('State is required'),
  zip: yup.string().required('ZIP code is required'),
  country: yup.string().required('Country is required'),
  taxId: yup.string().required('Tax ID is required'),
  billingEmail: yup
    .string()
    .email('Invalid email')
    .required('Billing email is required'),
});

export const paymentSchema = yup.object({
  cardholderName: yup.string().required('Cardholder name is required'),
  cardNumber: yup
    .string()
    .matches(/^\d{16}$/, 'Card number must be 16 digits')
    .required('Card number is required'),
  expiry: yup
    .string()
    .matches(/^(0[1-9]|1[0-2])\/\d{2}$/, 'Expiry must be MM/YY')
    .required('Expiry is required'),
  cvc: yup
    .string()
    .matches(/^\d{3}$/, 'CVC must be 3 digits')
    .required('CVC is required'),
  country: yup.string().required('Country is required'),
  zipCode: yup.string().required('ZIP code is required'),
});
export type MainType = 'self' | 'notSelf';
export type SubType = 'teammate' | 'other';

export interface AuthorizedSignatoryForm {
  mainType: MainType;
  subType: SubType;
  teammateId: string;
  fullName: string;
  email: string;
  confirmEmail: string;
}

export const validationSchema = yup.object({
  mainType: yup.string().required(),

  subType: yup.string().when('mainType', {
    is: 'notSelf',
    then: (schema) => schema.required('Please select one option'),
  }),

  teammateId: yup.string().when(['mainType', 'subType'], {
    is: (mainType: MainType, subType: SubType) =>
      mainType === 'notSelf' && subType === 'teammate',
    then: (schema) => schema.required('Teammate is required'),
  }),

  fullName: yup.string().when(['mainType', 'subType'], {
    is: (mainType: MainType, subType: SubType) =>
      mainType === 'notSelf' && subType === 'other',
    then: (schema) =>
      schema
        .min(3, 'Full Name must be at least 3 characters')
        .required('Full name is required'),
  }),

  email: yup.string().when(['mainType', 'subType'], {
    is: (mainType: MainType, subType: SubType) =>
      mainType === 'notSelf' && subType === 'other',
    then: (schema) =>
      schema.email('Invalid email').required('Email is required'),
  }),

  confirmEmail: yup.string().when(['mainType', 'subType'], {
    is: (mainType: MainType, subType: SubType) =>
      mainType === 'notSelf' && subType === 'other',
    then: (schema) =>
      schema
        .oneOf([yup.ref('email')], 'Emails must match')
        .required('Confirm email is required'),
  }),
});

export const upgradeSchema = yup.object().shape({
  agencyName: yup.string().required('Agency name is required'),
  contactName: yup.string().required('Contact name is required'),
  email: yup
    .string()
    .email('Please enter a valid email')
    .required('Email is required'),
  phoneNumber: yup
    .string()
    .required('Phone number is required')
    .min(10, 'Phone number is too short'),
  planInterest: yup.string().required('Please select a plan'),
  talentCount: yup.string().required('Please select talent count'),
  upgradeTimeline: yup.string().required('Please select a timeline'),
  additionalDetails: yup.string().max(500, 'Max 500 characters'),
});
